import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ApiError,
  setPlatformTokenProvider,
  setUnauthorizedHandler,
} from '@/lib/api/client';
import { authApi } from '@/lib/api/endpoints';
import type { ApiUser } from '@/lib/api/types';
import { clearConsultantSession } from '@/features/consultant/consultantSessionStore';
import { storageKeys } from '@/lib/config';
import { readStorage, removeStorage, writeStorage } from '@/lib/storage';
import type { AuthStatus, AuthUser, RoleCode } from '@/types';

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  status: AuthStatus;
  isPending: boolean;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  resetPassword: (identifier: string) => Promise<string>;
  refreshUser: () => Promise<AuthUser | null>;
  applyUser: (payload: ApiUser) => AuthUser;
  switchRole: (role: RoleCode) => void;
  hasRole: (role: RoleCode) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const toAuthUser = (payload: ApiUser, activeRole?: RoleCode): AuthUser => {
  const roles = (Array.isArray(payload.roles) ? payload.roles : []) as RoleCode[];
  const categories = Array.isArray(payload.categories) ? payload.categories : [];
  const resolved = activeRole && roles.includes(activeRole) ? activeRole : roles[0];
  return {
    id: payload.id,
    academiaId: payload.academia_id,
    email: payload.email,
    firstName: payload.first_name,
    lastName: payload.last_name,
    fullName: payload.full_name,
    initials: payload.initials,
    position: payload.position,
    department: payload.department,
    avatarUrl: payload.avatar_url,
    phone: payload.phone ?? null,
    altEmail: payload.alt_email ?? null,
    preferredLanguage: payload.preferred_language || 'RU',
    showOnline: payload.show_online ?? true,
    roles,
    categories,
    activeRole: resolved ?? 'RESEARCHER',
  };
};

const normalizeStoredUser = (value: AuthUser | null): AuthUser | null => {
  if (!value || typeof value !== 'object') return null;
  const roles = Array.isArray(value.roles) ? (value.roles as RoleCode[]) : [];
  const categories = Array.isArray(value.categories) ? value.categories : [];
  const activeRole =
    value.activeRole && roles.includes(value.activeRole) ? value.activeRole : roles[0];
  return {
    ...value,
    phone: value.phone ?? null,
    altEmail: value.altEmail ?? null,
    preferredLanguage: value.preferredLanguage || 'RU',
    showOnline: value.showOnline ?? true,
    roles,
    categories,
    activeRole: activeRole ?? 'RESEARCHER',
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(() =>
    normalizeStoredUser(readStorage<AuthUser | null>(storageKeys.session, null)),
  );
  const [token, setToken] = useState<string | null>(() =>
    readStorage<string | null>(storageKeys.token, null),
  );
  const [isPending, setIsPending] = useState(false);

  // Register before child effects fire so first platform API calls see the token.
  setPlatformTokenProvider(() => readStorage<string | null>(storageKeys.token, null));

  useEffect(() => {
    if (user) writeStorage(storageKeys.session, user);
    else removeStorage(storageKeys.session);
  }, [user]);

  useEffect(() => {
    if (token) writeStorage(storageKeys.token, token);
    else removeStorage(storageKeys.token);
  }, [token]);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    removeStorage(storageKeys.session);
    removeStorage(storageKeys.token);
    clearConsultantSession();
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authApi
      .me()
      .then((payload) => {
        if (!cancelled) setUser((current) => toAuthUser(payload, current?.activeRole));
      })
      .catch((error: unknown) => {
        if (!cancelled && error instanceof ApiError && error.status === 401) clearSession();
      });
    return () => {
      cancelled = true;
    };
  }, [clearSession, token]);

  const login = useCallback(async (identifier: string, password: string) => {
    setIsPending(true);
    try {
      const session = await authApi.login(identifier, password);
      writeStorage(storageKeys.token, session.access_token);
      const nextUser = toAuthUser(session.user);
      setToken(session.access_token);
      setUser(nextUser);
      return nextUser;
    } finally {
      setIsPending(false);
    }
  }, []);

  const logout = useCallback(() => {
    void authApi.logout().catch(() => undefined);
    clearSession();
  }, [clearSession]);

  const resetPassword = useCallback(async (identifier: string) => {
    setIsPending(true);
    try {
      const response = await authApi.requestPasswordReset(identifier);
      return response.detail;
    } finally {
      setIsPending(false);
    }
  }, []);

  const applyUser = useCallback((payload: ApiUser) => {
    const next = toAuthUser(payload, user?.activeRole);
    setUser(next);
    return next;
  }, [user?.activeRole]);

  const refreshUser = useCallback(async () => {
    if (!token) return null;
    const payload = await authApi.me();
    return applyUser(payload);
  }, [applyUser, token]);

  const switchRole = useCallback((role: RoleCode) => {
    setUser((current) =>
      current && current.roles.includes(role) ? { ...current, activeRole: role } : current,
    );
  }, []);

  const hasRole = useCallback(
    (role: RoleCode) => Boolean(user?.roles.includes(role)),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      status: user ? 'authenticated' : 'anonymous',
      isPending,
      login,
      logout,
      resetPassword,
      refreshUser,
      applyUser,
      switchRole,
      hasRole,
    }),
    [applyUser, hasRole, isPending, login, logout, refreshUser, resetPassword, switchRole, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
